import {
  ChakraBox,
  ChakraFlex,
  CircularProgress,
  EquipUITheme,
  FAIconName,
  FileIconsV2,
  getFileIconV2,
  Icon,
  Text,
} from '@equip.health/ui';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  AttachmentType,
  NoteAttachmentStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { useNoteAttachmentModalContext } from '~/lib/contexts/NoteAttachmentModalContext';
import fileUtil, { getFileExtension } from '~/lib/util/file.util';
import { useLazyGetPatientDocumentDownloadUrlQuery } from '~/store/services/documents';

type NoteAttachmentCardProps = {
  document: AttachmentType;
  isNoteAuthor: boolean;
  isAddendumModal?: boolean;
  patientId: string;
  setUploadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
};

const { label } = EquipUITheme.typography;

const NoteAttachmentCard = ({
  document,
  isNoteAuthor,
  isAddendumModal = false,
  patientId,
  setUploadedFiles,
}: NoteAttachmentCardProps) => {
  const [getNoteAttachmentDownloadUrl, { isFetching }] =
    useLazyGetPatientDocumentDownloadUrlQuery();
  const { setShowNoteAttachmentModal } = useNoteAttachmentModalContext();
  const [downloadFailed, setDownloadFailed] = useState<boolean>(false);
  const handleDelete = () => {
    setUploadedFiles((previous) =>
      previous.filter(
        (uploadedFile: AttachmentType) =>
          uploadedFile.attachmentExternalId !== document.attachmentExternalId,
      ),
    );
  };

  const handleAttachmentClick = async () => {
    const isClickable =
      document.fileName &&
      document.attachmentExternalId &&
      patientId &&
      (document.statusCode === NoteAttachmentStatus.UPLOADED ||
        document.statusCode === null);

    if (!isClickable) return;
    setDownloadFailed(false);
    try {
      const response = await getNoteAttachmentDownloadUrl({
        documentExternalId: document.attachmentExternalId,
        fileName: document.fileName,
        patientId,
      }).unwrap();

      if (!response?.isValidFile || !response?.attachmentDownloadUrl) {
        setDownloadFailed(true);
        return;
      }
      const fileUrl = response.attachmentDownloadUrl;
      if (fileUtil.canShowPreview(document.fileName)) {
        setShowNoteAttachmentModal({
          name: document.fileName,
          url: fileUrl,
        });
      } else {
        window.open(fileUrl, '_blank', 'noopener');
      }
    } catch {
      setDownloadFailed(true);
    }
  };

  const fileStatusText =
    getFileExtension(document.fileName)?.toUpperCase() ?? '';

  return (
    <ChakraBox>
      <ChakraFlex
        alignItems="center"
        background="neutral.background.primary"
        border="1px solid"
        borderColor="neutral.line"
        borderRadius="16px"
        data-testid={`note-attachment-card__${document.attachmentExternalId}`}
        height="56px"
        id={`note-attachment-card__${document.attachmentExternalId}`}
        padding="0 16px"
      >
        <FileIconsV2
          height="40px"
          icon={getFileIconV2(document.fileName)}
          width="40px"
        />
        <ChakraBox
          color="neutral.secondary"
          cursor={document.attachmentExternalId ? 'pointer' : 'default'}
          flex="1"
          minWidth="0"
          overflow="hidden"
          margin="0 12px"
          onClick={handleAttachmentClick}
        >
          <Text
            color="neutral.primary"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {document.fileName}
          </Text>
          <ChakraFlex {...label} lineHeight="14px">
            {!isFetching && (
              <>
                <Text marginRight="4px" color="neutral.secondary">
                  {fileStatusText}
                </Text>
                {!downloadFailed && (
                  <Text color="neutral.secondary">
                    {fileUtil.isImageFile(document.fileName)
                      ? ' Image'
                      : ' Document'}
                  </Text>
                )}
              </>
            )}
          </ChakraFlex>
        </ChakraBox>
        {isFetching && (
          <CircularProgress
            id={`patient-note-attachment-loading__${document.attachmentExternalId}`}
            isIndeterminate
            size="30px"
          />
        )}
        {(isNoteAuthor || isAddendumModal) && (
          <ChakraBox cursor="pointer" onClick={handleDelete}>
            <Icon
              aria-label="Delete attachment"
              color="neutral.secondary"
              name={FAIconName.timesRegular}
              size="md"
            />
          </ChakraBox>
        )}
      </ChakraFlex>
      {downloadFailed && (
        <Text color="critical.base" variant="caption">
          Unable to view attachment.
        </Text>
      )}
    </ChakraBox>
  );
};
export default NoteAttachmentCard;
